import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setToken, clearToken, getToken, setCsrfToken } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Store user in sessionStorage
const getUserFromStorage = () => {
  try {
    const stored = sessionStorage.getItem('simla_user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const setUserInStorage = (user) => {
  try {
    if (user) {
      sessionStorage.setItem('simla_user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('simla_user');
    }
  } catch {}
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(getUserFromStorage);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    // If no token stored, don't try to load user
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const res = await authAPI.me();
      setUser(res.data);
      setUserInStorage(res.data);
    } catch (err) {
      console.log('Auth check failed:', err?.message || 'Unknown error');
      // Token might be invalid - clear everything
      setUser(null);
      setUserInStorage(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    loadUser();
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    if (res.data.csrf_token) setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setUserInStorage(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await authAPI.register({ email, password, name });
    if (res.data.csrf_token) setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setUserInStorage(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (e) {}
    setUser(null);
    setUserInStorage(null);
    clearToken();
  };

  const hasRole = (...roles) => user && roles.includes(user.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};
